import React, { useState } from 'react';
import useNewQuestion from '../../../hooks/useNewQuestion';
import { FormContainer, FormInput, FormTextarea, TagsDisplay } from '../../form';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import Input from '../../ui/input';

/**
 * NewQuestionPage component allows users to submit a new question with a title,
 * description, tags, and username.
 */
const NewQuestionPage = () => {
  const [inputValue, setInputValue] = useState('');
  const {
    title,
    setTitle,
    text,
    setText,
    tags,
    addTag,
    removeTag,
    titleErr,
    textErr,
    tagErr,
    postQuestion,
  } = useNewQuestion();

  // State for AI-related features
  const [aiModel, setAiModel] = useState('gpt-4'); // Default AI model
  const [aiSolution, setAiSolution] = useState<string | null>(null);

  const [similarQuestions, setSimilarQuestions] = useState<string[]>([]);
  const [similarityScore, setSimilarityScore] = useState<number | null>(null);

  // mock function to simulate AI solution generation
  const generateAiSolution = () => {
    if (title && text) {
      setAiSolution(`Generated solution for "${title}" using ${aiModel}`);
      setSimilarQuestions(['How to implement feature X?']);
      setSimilarityScore(85);
    }
  };

  const handleTagInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setInputValue(value);
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue.trim());
      setInputValue('');
    }
  };

  return (
    <FormContainer>
      <FormInput
        title={'Question Title'}
        hint={'Limit title to 100 characters or less'}
        id={'formTitleInput'}
        val={title}
        setState={setTitle}
        err={titleErr}
      />
      <FormTextarea
        title={'Question Text'}
        hint={'Add details'}
        id={'formTextInput'}
        val={text}
        setState={setText}
        err={textErr}
      />

      <div className='mb-4'>
        <label htmlFor='tags' className='block font-medium mb-1 text-sm'>
          Tags <span className='text-red-500 ml-1'>*</span>
        </label>
        <p className='text-xs text-gray-500 mb-2'>Add up to 5 tags to categorize your question</p>

        <div className='flex flex-wrap items-center gap-2 p-2 border rounded-md bg-white min-h-[38px]'>
          <TagsDisplay tags={tags} onTagClick={removeTag} className='flex-wrap gap-2' />

          <Input
            id='tags'
            placeholder={
              tags.length === 0
                ? 'Type and press Enter to add a tag. Click on a tag to delete it'
                : ''
            }
            className='flex-grow border-0 focus-visible:ring-0 focus-visible:ring-offset-0 pl-0'
            value={inputValue}
            onChange={handleTagInput}
            onKeyDown={handleTagKeyDown}
          />
        </div>
        {tagErr && <p className='text-xs text-red-500 mt-1'>{tagErr}</p>}
      </div>

      {/* AI-Suggested Solution Section */}
      <div className='mt-6'>
        <h3 className='text-lg font-semibold'>AI-Suggested Solution</h3>
        <div className='relative inline-block text-left'>
          <button
            className='bg-white text-gray-700 hover:bg-gray-100 py-2 px-4 border border-gray-300 rounded'
            id='ai-model-button'
            aria-haspopup='true'
            aria-expanded='true'
            onClick={() => {
              const dropdown = document.getElementById('ai-model-dropdown');
              if (dropdown) {
                dropdown.classList.toggle('hidden');
              }
            }}>
            {aiModel}
            <svg
              className='ml-2 h-5 w-5'
              xmlns='http://www.w3.org/2000/svg'
              viewBox='0 0 20 20'
              fill='currentColor'
              aria-hidden='true'>
              <path
                fillRule='evenodd'
                d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
                clipRule='evenodd'
              />
            </svg>
          </button>
          <div
            id='ai-model-dropdown'
            className='origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none hidden'
            role='menu'
            aria-orientation='vertical'
            aria-labelledby='ai-model-button'>
            <button
              className='block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              role='menuitem'
              onClick={() => {
                setAiModel('gpt-4');
                const dropdown = document.getElementById('ai-model-dropdown');
                if (dropdown) {
                  dropdown.classList.add('hidden');
                }
              }}>
              GPT-4
            </button>
            <button
              className='block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              role='menuitem'
              onClick={() => {
                setAiModel('a');
                const dropdown = document.getElementById('ai-model-dropdown');
                if (dropdown) {
                  dropdown.classList.add('hidden');
                }
              }}>
              a
            </button>
            <button
              className='block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              role='menuitem'
              onClick={() => {
                setAiModel('b');
                const dropdown = document.getElementById('ai-model-dropdown');
                if (dropdown) {
                  dropdown.classList.add('hidden');
                }
              }}>
              b
            </button>
          </div>
        </div>
        <Button onClick={generateAiSolution} className='bg-green-500 hover:bg-green-600 mt-4'>
          Generate Solution
        </Button>
        {/* Voting buttons */}
        {aiSolution && (
          <div className='flex gap-2 mt-2'>
            <Button className='bg-green-100 hover:bg-green-200'>👍</Button>
            <Button className='bg-red-100 hover:bg-red-200'>👎</Button>
          </div>
        )}
      </div>
      {/* Similar/Repeat Questions Section */}
      {similarQuestions.length > 0 && (
        <div className='mt-6'>
          <h3 className='text-lg font-semibold'>Similar Questions</h3>
          <ul className='list-disc list-inside'>
            {similarQuestions.map((question, index) => (
              <li key={index}>{question}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Similarity Score Section */}
      {similarityScore !== null && (
        <div className='mt-6'>
          <h3 className='text-lg font-semibold'>Similarity Score</h3>
          <p>{similarityScore}% similar to existing questions.</p>
        </div>
      )}
      <div className='flex flex-col sm:flex-row sm:justify-between items-center mt-6'>
        <Button onClick={postQuestion} className='bg-blue-500 hover:bg-blue-600 mb-4 sm:mb-0'>
          Post Question
        </Button>
        <Alert className='bg-gray-100 border-gray-200 p-2'>
          <AlertDescription className='text-xs text-gray-600'>
            * indicates mandatory fields
          </AlertDescription>
        </Alert>
      </div>
    </FormContainer>
  );
};

export default NewQuestionPage;
